import { type BackupRecording, type SessionRecordingConfig } from '../../types';
import { serializeBlob } from '../../utils';
import { logger } from './logger';

interface Props {
  config: SessionRecordingConfig;
  sessionId: string;
  recordingId: string;
  stream: MediaStream;
  onBackupAvailable: (backup: BackupRecording) => Promise<void>;
}

export class BackupRecorder {
  doneRecording = false;
  private mediaRecorder: MediaRecorder;
  private stopPromise: Promise<void> = Promise.resolve();
  private resolveStop: (() => void) | null = null;

  constructor(private props: Props) {
    const mimeType = `audio/${props.config.format};codecs=${props.config.codec}`;
    this.mediaRecorder = new MediaRecorder(this.props.stream, { mimeType: mimeType });

    this.mediaRecorder.ondataavailable = async (event) => {
      try {
        const { props } = this;
        await props.onBackupAvailable({
          isProcessed: false,
          sessionId: props.sessionId,
          recordingId: props.recordingId,
          blob: await serializeBlob(event.data),
        });

        logger.info({
          event_type: 'BACKUP_RECORDED',
          recording_id: props.recordingId,
          backup_size_bytes: event.data.size,
        });
      } finally {
        this.doneRecording = true;
        this.resolveFinalStop();
      }
    };

    this.mediaRecorder.start();
  }

  stop(): Promise<void> {
    if (this.mediaRecorder.state === 'inactive') return this.stopPromise;
    this.stopPromise = new Promise<void>((resolve) => {
      this.resolveStop = resolve;
    });
    this.mediaRecorder.stop();
    return this.stopPromise;
  }

  /** Stops capture without publishing the final full-recording blob (logout). */
  discard() {
    this.mediaRecorder.ondataavailable = null;
    this.mediaRecorder.onerror = null;
    if (this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
    this.doneRecording = true;
    this.resolveFinalStop();
  }

  private resolveFinalStop(): void {
    this.resolveStop?.();
    this.resolveStop = null;
  }
}
